import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import Navigation from "@/components/Navigation";
import { ArrowLeft, AlertTriangle, Phone, MapPin, Clipboard, Heart, ExternalLink, PawPrint, Share2, MessageCircle, Mail, Copy, Check, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return d; }
}

export default function EmergencyMode() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pets, setPets] = useState([]);
  const [selectedPetId, setSelectedPetId] = useState("");
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [shareLoading, setShareLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetch(`${API}/pets`, { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then(data => { setPets(data); if (data.length > 0) setSelectedPetId(data[0].pet_id); setLoading(false); });
  }, []);

  const pet = pets.find(p => p.pet_id === selectedPetId);

  const handleFindVet = () => {
    const query = "emergency vet near me";
    window.open(`https://www.google.com/maps/search/${encodeURIComponent(query)}`, "_blank");
  };

  const ensureShareLink = async () => {
    if (!pet) return "";
    if (pet.share_token) {
      return `${window.location.origin}/passport/share/${pet.share_token}`;
    }
    setShareLoading(true);
    try {
      const res = await fetch(`${API}/pets/${pet.pet_id}/generate-share`, { method: "POST", credentials: "include" });
      if (!res.ok) throw new Error("share failed");
      const data = await res.json();
      setPets(prev => prev.map(p => p.pet_id === pet.pet_id ? { ...p, share_token: data.share_token } : p));
      return `${window.location.origin}/passport/share/${data.share_token}`;
    } catch {
      return "";
    } finally {
      setShareLoading(false);
    }
  };

  const openShareSheet = async () => {
    const url = await ensureShareLink();
    if (!url) { alert(t("emergency.shareFailed")); return; }
    setShareUrl(url);
    setShareOpen(true);
    setCopied(false);
  };

  const shareMessage = () => {
    const name = pet?.name || t("emergency.ownerContact");
    return t("emergency.shareMessage", { name, url: shareUrl });
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareMessage())}`, "_blank");
  };
  const handleSMS = () => {
    window.location.href = `sms:?&body=${encodeURIComponent(shareMessage())}`;
  };
  const handleEmail = () => {
    const subject = t("emergency.share", { name: pet?.name || "" });
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(shareMessage())}`;
  };
  const handleCopy = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        // Fallback for non-secure contexts / older browsers
        const ta = document.createElement("textarea");
        ta.value = shareUrl;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Copy this link:", shareUrl);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ background: "#FAF9F6" }}>
        <Navigation />
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="h-64 rounded-3xl shimmer-bg" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen font-['Figtree']" style={{ background: "#FAF9F6" }}>
      <Navigation />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-4 mb-6 animate-fade-in-up">
          <button onClick={() => navigate("/dashboard")} className="p-2 rounded-xl hover:bg-white hover:shadow-sm text-slate-500 transition-all">
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-extrabold text-slate-800 font-['Nunito']">{t("emergency.title")}</h1>
              <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
            </div>
            <p className="text-slate-500 text-sm">{t("emergency.subtitle")}</p>
          </div>
        </div>

        {/* BIG Emergency Button */}
        <div className="mb-6 animate-fade-in-up delay-100">
          <button
            data-testid="find-vet-btn"
            onClick={handleFindVet}
            className="w-full py-5 rounded-3xl bg-gradient-to-r from-[#EF5350] to-[#E53935] text-white font-extrabold text-xl shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all flex items-center justify-center gap-4 animate-pulse-glow font-['Nunito']"
          >
            <AlertTriangle size={28} strokeWidth={2.5} />
            {t("emergency.findVet")}
            <ExternalLink size={20} />
          </button>
          <p className="text-center text-xs text-slate-400 mt-2">{t("emergency.findVetHint")}</p>
        </div>

        {/* Call direct */}
        {pet?.vet_phone && (
          <div className="mb-6 animate-fade-in-up delay-200">
            <a
              href={`tel:${pet.vet_phone}`}
              className="w-full py-4 rounded-3xl bg-[#FFF5E6] border-2 border-[#FF8A65] text-[#FF8A65] font-bold text-base shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all flex items-center justify-center gap-3 font-['Nunito']"
              data-testid="call-vet-btn"
            >
              <Phone size={20} strokeWidth={2.5} />
              {t("emergency.callVet", { vet: pet.vet_name || t("emergency.yourVet"), phone: pet.vet_phone })}
            </a>
          </div>
        )}

        {/* Share Emergency Info */}
        {pet && (
          <div className="mb-6 animate-fade-in-up delay-200">
            <button
              data-testid="share-emergency-btn"
              onClick={openShareSheet}
              disabled={shareLoading}
              className="w-full py-4 rounded-3xl bg-white border-2 border-slate-200 text-slate-700 font-semibold text-base shadow-sm hover:border-[#42A5F5] hover:text-[#1565C0] transition-all flex items-center justify-center gap-3 font-['Nunito'] disabled:opacity-60"
            >
              <Share2 size={18} strokeWidth={2.5} />
              {shareLoading ? t("emergency.preparingLink") : t("emergency.share", { name: pet.name })}
            </button>
          </div>
        )}

        {/* Pet selector (if multiple pets) */}
        {pets.length > 1 && (
          <div className="mb-6 animate-fade-in-up delay-200">
            <label className="block text-xs font-semibold text-slate-600 mb-2">{t("emergency.showingInfoFor")}</label>
            <div className="flex gap-2 flex-wrap">
              {pets.map(p => (
                <button
                  key={p.pet_id}
                  onClick={() => setSelectedPetId(p.pet_id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all ${selectedPetId === p.pet_id ? "bg-[#FF8A65] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-[#FF8A65]"}`}
                >
                  <PawPrint size={14} /> {p.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* No pets state */}
        {pets.length === 0 && (
          <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-8 text-center animate-fade-in-up">
            <PawPrint size={36} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500 font-medium">{t("emergency.noPets")}</p>
            <button onClick={() => navigate("/dashboard")} className="mt-4 px-6 py-2.5 rounded-full bg-[#FF8A65] text-white text-sm font-semibold">{t("emergency.addAPet")}</button>
          </div>
        )}

        {/* Pet medical summary */}
        {pet && (
          <div className="space-y-4 animate-fade-in-up delay-300">
            {/* Identity card */}
            <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-6">
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-4">{t("emergency.petIdentity")}</h3>
              <div className="flex items-center gap-4 mb-4">
                {pet.photo_url ? (
                  <img src={pet.photo_url} alt={pet.name} className="w-16 h-16 rounded-2xl object-cover" />
                ) : (
                  <div className="w-16 h-16 rounded-2xl bg-[#FFF5E6] flex items-center justify-center">
                    <PawPrint size={28} className="text-[#FF8A65]" />
                  </div>
                )}
                <div>
                  <h2 className="text-xl font-extrabold text-slate-800 font-['Nunito']">{pet.name}</h2>
                  <p className="text-slate-500 capitalize">{pet.species}{pet.breed ? ` · ${pet.breed}` : ""}</p>
                  {pet.color && <p className="text-sm text-slate-400">{pet.color}</p>}
                </div>
              </div>
              {pet.microchip_id && (
                <div className="flex items-center gap-3 p-3 rounded-xl bg-[#E3F2FD]">
                  <Clipboard size={15} className="text-[#42A5F5]" />
                  <span className="text-sm font-medium text-slate-700">{t("emergency.microchip")} <strong>{pet.microchip_id}</strong></span>
                </div>
              )}
            </div>

            {/* Critical medical info */}
            {pet.medical_notes && (
              <div className="bg-[#FFEBEE] rounded-3xl border border-red-200 p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center">
                    <Heart size={16} className="text-red-500" />
                  </div>
                  <h3 className="font-bold text-red-800 font-['Nunito']">{t("emergency.criticalMedical")}</h3>
                </div>
                <p className="text-sm text-red-900 leading-relaxed font-medium">{pet.medical_notes}</p>
              </div>
            )}

            {/* Vaccinations */}
            {pet.vaccinations?.length > 0 && (
              <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6">
                <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-4">{t("emergency.vaccinations")}</h3>
                <div className="space-y-2">
                  {pet.vaccinations.map((v, i) => (
                    <div key={`${v.name || "vax"}-${v.date || i}`} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                      <span className="text-sm font-medium text-slate-700">{v.name}</span>
                      <span className="text-xs bg-[#E8F5E9] text-[#388E3C] px-2.5 py-1 rounded-full font-medium">{formatDate(v.date)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Vet contact */}
            {(pet.vet_name || pet.vet_phone || pet.vet_address) && (
              <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6">
                <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-4">{t("emergency.veterinarianContact")}</h3>
                <div className="space-y-3">
                  {pet.vet_name && (
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#E8F5E9] flex items-center justify-center">
                        <Heart size={14} className="text-[#66BB6A]" />
                      </div>
                      <span className="font-medium text-slate-800">{pet.vet_name}</span>
                    </div>
                  )}
                  {pet.vet_phone && (
                    <a href={`tel:${pet.vet_phone}`} className="flex items-center gap-3 hover:bg-slate-50 rounded-xl p-1 -m-1 transition-colors">
                      <div className="w-8 h-8 rounded-full bg-[#E3F2FD] flex items-center justify-center">
                        <Phone size={14} className="text-[#42A5F5]" />
                      </div>
                      <span className="text-[#1565C0] font-medium">{pet.vet_phone}</span>
                    </a>
                  )}
                  {pet.vet_address && (
                    <a
                      href={`https://www.google.com/maps/search/${encodeURIComponent(pet.vet_address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-3 hover:bg-slate-50 rounded-xl p-1 -m-1 transition-colors"
                    >
                      <div className="w-8 h-8 rounded-full bg-[#FFF5E6] flex items-center justify-center flex-shrink-0 mt-0.5">
                        <MapPin size={14} className="text-[#FF8A65]" />
                      </div>
                      <span className="text-sm text-slate-600">{pet.vet_address}</span>
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Emergency contacts */}
            {pet.emergency_contacts?.length > 0 && (
              <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6" data-testid="emergency-contacts-card">
                <div className="flex items-center gap-2 mb-4">
                  <Users size={14} className="text-slate-400" />
                  <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">{t("emergency.emergencyContacts")}</h3>
                </div>
                <div className="space-y-3">
                  {pet.emergency_contacts.map((c, i) => (
                    <a
                      key={`ec-${i}`}
                      data-testid={`emergency-contact-${i}`}
                      href={c.phone ? `tel:${c.phone}` : undefined}
                      className="flex items-center gap-3 p-3 rounded-2xl hover:bg-slate-50 transition-colors border border-slate-100"
                    >
                      <div className="w-10 h-10 rounded-full bg-[#FFF5E6] flex items-center justify-center flex-shrink-0">
                        <Phone size={15} className="text-[#FF8A65]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-800 truncate">{c.name || t("emergency.unnamedContact")}</span>
                          {c.relationship && (
                            <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-medium">{c.relationship}</span>
                          )}
                        </div>
                        {c.phone && <p className="text-sm text-[#1565C0] font-medium mt-0.5">{c.phone}</p>}
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Owner info */}
            <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6">
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-4">{t("emergency.ownerContact")}</h3>
              <div className="flex items-center gap-4">
                {user?.picture ? (
                  <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-[#FF8A65] flex items-center justify-center text-white font-bold">{user?.name?.[0]}</div>
                )}
                <div>
                  <p className="font-semibold text-slate-800">{user?.name}</p>
                  <p className="text-sm text-slate-500">{user?.email}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Share Sheet */}
      {shareOpen && (
        <div
          data-testid="share-sheet-overlay"
          onClick={() => setShareOpen(false)}
          className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4 animate-fade-in"
        >
          <div
            onClick={e => e.stopPropagation()}
            className="bg-white rounded-3xl w-full sm:max-w-md p-6 shadow-2xl animate-fade-in-up"
            data-testid="share-sheet"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-[#E3F2FD] flex items-center justify-center">
                <Share2 size={18} className="text-[#1565C0]" />
              </div>
              <div>
                <h3 className="font-extrabold text-slate-800 font-['Nunito']">{t("emergency.shareSheetTitle")}</h3>
                <p className="text-xs text-slate-500">{t("emergency.shareSheetSub")}</p>
              </div>
            </div>

            <div className="rounded-2xl bg-slate-50 border border-slate-200 p-3 mb-5 flex items-center gap-2">
              <span className="text-xs font-mono text-slate-600 truncate flex-1" data-testid="share-url">{shareUrl}</span>
              <button
                data-testid="share-copy-btn"
                onClick={handleCopy}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold ${copied ? "bg-[#E8F5E9] text-[#388E3C]" : "bg-white border border-slate-200 text-slate-700 hover:border-[#FF8A65]"}`}
              >
                {copied ? <><Check size={13} /> {t("common.copied")}</> : <><Copy size={13} /> {t("common.copy")}</>}
              </button>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-5">
              <button
                data-testid="share-whatsapp-btn"
                onClick={handleWhatsApp}
                className="flex flex-col items-center gap-2 p-3 rounded-2xl bg-[#E8F5E9] hover:bg-[#C8E6C9] transition-colors"
              >
                <MessageCircle size={22} className="text-[#2E7D32]" />
                <span className="text-xs font-semibold text-slate-700">WhatsApp</span>
              </button>
              <button
                data-testid="share-sms-btn"
                onClick={handleSMS}
                className="flex flex-col items-center gap-2 p-3 rounded-2xl bg-[#E3F2FD] hover:bg-[#BBDEFB] transition-colors"
              >
                <MessageCircle size={22} className="text-[#1565C0]" />
                <span className="text-xs font-semibold text-slate-700">SMS</span>
              </button>
              <button
                data-testid="share-email-btn"
                onClick={handleEmail}
                className="flex flex-col items-center gap-2 p-3 rounded-2xl bg-[#FFF5E6] hover:bg-[#FFE8C7] transition-colors"
              >
                <Mail size={22} className="text-[#FF8A65]" />
                <span className="text-xs font-semibold text-slate-700">Email</span>
              </button>
            </div>

            <button
              data-testid="share-close-btn"
              onClick={() => setShareOpen(false)}
              className="w-full py-2.5 rounded-full border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50"
            >
              {t("common.close")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
